// Uneltele asistentului pe PLANUL DE SITUAȚIE. Lucrează pe SOLUȚIA DESCHISĂ,
// ca cele de fațadă (`unelteFatada.ts`), dar cu UN SINGUR DRUM: pe plan nu
// există schimbare mică făcută de asistent. Circulația, locurile, trotuarele
// și zonele le desenează agentul planului (jobul `site_plan_agent`), care
// verifică fiecare lot pe reguli și rescrie documentul lot cu lot; o scriere
// directă din browser ar fi ocolit verificarea și ar fi pus pe planșă ce
// agentul n-ar fi acceptat. Asistentul citește și predă.
import type { Unealta } from "./unelte";
import { ALTA_SOLUTIE } from "./unelteFatada";
import type { AgentPlanRezumat } from "../viewer/plan_agent/firPlan";

/** Ce știe panoul agentului de plan și ce poate face — ținut stabil cu `useRef`. */
export type ContextSituatie = {
  /** Planul deschis: rezumatul ultimei rulări a agentului (lipsă pe un plan pe
   *  care n-a lucrat), dacă lucrează acum, ultimele lui propoziții pentru om. */
  stare: () => { plan: { id: string; nume: string | null };
                 agent: AgentPlanRezumat | null;
                 agent_lucreaza: boolean;
                 /** DATELE PROIECTULUI (`plan_agent/datePlan.ts`): indicatorii cu
                  *  plafonul proiectului și realizatul soluției, separat; terenul,
                  *  clădirile, parcarea, abaterile. */
                 proiect?: Record<string, unknown>;
                 ultimele_propozitii: string[];
                 /** SELECȚIA DE PE PLANȘĂ: fișa obiectului ales (un loc cu numărul
                  *  lui, o alee, un trotuar, o zonă — v. `plan_agent/fisaSituatie.ts`);
                  *  goală = nimic ales. */
                 selectie?: unknown[] };
  /** Pornește agentul în modul de EDITARE, cu cererea omului, dacă se cere
   *  ultima poză din discuție, și selecția de pe planșă (fișa de mai sus).
   *  `null` fără drept de scriere. */
  predaAgentului: ((cerere: string, cuPoza: boolean, cuSelectia: boolean)
                   => Promise<{ ok: boolean; motiv?: string }>) | null;
};

/** CONTEXTUL LEGAT DE SOLUȚIA LUI, ca la fațadă: un tur pornit pe planul A,
 *  ajuns cu uneltele după ce ecranul a trecut pe B, ar fi pornit agentul pe B.
 *  Fiecare funcție întreabă întâi dacă soluția de pe ecran mai e cea pe care
 *  s-a legat; dacă nu, refuză cu `ALTA_SOLUTIE`. */
export function legatDeSolutie(ctx: ContextSituatie, id: string | null,
                               curent: () => string | null): ContextSituatie {
  const pazit = <A extends unknown[], R>(f: (...a: A) => R) => (...a: A): R => {
    if (curent() !== id) throw new Error(ALTA_SOLUTIE);
    return f(...a);
  };
  return {
    stare: pazit(ctx.stare),
    predaAgentului: ctx.predaAgentului ? pazit(ctx.predaAgentului) : null,
  };
}

/** Ce poate cere omul, spus modelului o dată, nu ghicit de el: lista e a
 *  agentului (v. `positioning_plan/agent_plan`). */
const CE_POATE_FACE = [
  "o alee în plus sau mutată, o fundătură întoarsă, aleea de pompieri",
  "locuri de parcare în plus, mutate, scoase; «mai multe locuri»",
  "trotuare și o trecere de pietoni într-un loc anume",
  "zonele exterioare: locul de joacă, spațiul verde, platforma de gunoi, în alt loc sau altă mărime",
  "«mai puțin asfalt», «mai mult verde»",
];

export function unelteSituatie(ctx: ContextSituatie): Unealta[] {
  return [
    {
      nume: "citeste_planul",
      nucleu: true,
      descriere:
        "Citește PROIECTUL și PLANUL DE SITUAȚIE deschis. `proiect`: indicatorii urbanistici "
        + "(POT, CUT, regimul de înălțime — fiecare cu `maxim_admis_pe_proiect`, plafonul din "
        + "regulament, și `realizat_pe_solutia_deschisa`, cât atinge soluția de pe ecran), "
        + "desfășurata supraterană și subterană, zonele de regim, terenul și bilanțul verde, "
        + "apartamentele, corpurile, nivelurile, parcarea (necesar, livrat pe sol și în subsol), "
        + "abaterile de la reguli. `agent`: locurile de parcare (față de țintă), spațiul verde "
        + "(față de cotă), loturile, blocajele rămase nerezolvate; dacă se lucrează acum, ultimele "
        + "propoziții ale rulării, ce a ales omul pe planșă (locul, aleea, trotuarul, zona) și ce "
        + "poate cere omul. Începe de aici la orice întrebare.",
      parametri: { type: "object", additionalProperties: false, properties: {} },
      ruleaza: () => {
        const st = ctx.stare();
        return { continut: { plan: st.plan, proiect: st.proiect ?? {},
                             agent: st.agent, agent_lucreaza: st.agent_lucreaza,
                             ultimele_propozitii: st.ultimele_propozitii,
                             selectie: st.selectie ?? [],
                             ce_poate_cere_omul: CE_POATE_FACE } };
      },
    },
    {
      nume: "preda_agentului_plan",
      nucleu: true,
      schimba: true,
      descriere:
        "Dă cererea AGENTULUI PLANULUI DE SITUAȚIE, care o face pe planul deschis, lot cu lot, "
        + "cu verificare pe reguli (câteva minute; planșa se schimbă pe măsură ce scrie). Orice "
        + "schimbare a planului trece pe aici: alei, locuri, trotuare, treceri, zone, «mai multe "
        + "locuri», «mai puțin asfalt». Cât lucrează, discuția e oprită. Nu întreba omul înainte: dă-i-o.",
      parametri: {
        type: "object", additionalProperties: false, required: ["cerere"],
        properties: {
          cerere: { type: "string", description: "cuvintele omului, întregi, plus numai ce s-a lămurit în discuție — nu o descriere a ta a planului de acum" },
          cu_poza: { type: "boolean", description: "trimite și ultima poză atașată de om (o schiță, o planșă de referință)" },
          cu_selectia: { type: "boolean", description: "trimite agentului și ce a ales omul pe planșă (locul, aleea, trotuarul, zona apăsate), când cererea e despre el; implicit da" },
        },
      },
      ruleaza: async (arg: { cerere: string; cu_poza?: boolean; cu_selectia?: boolean }) => {
        if (!ctx.predaAgentului) return { eroare: "nu e niciun plan de situație deschis pe care să pot porni agentul" };
        if (ctx.stare().agent_lucreaza) return { eroare: "o schimbare e deja în lucru pe planul ăsta; omul așteaptă să se termine" };
        const r = await ctx.predaAgentului(arg.cerere, !!arg.cu_poza, arg.cu_selectia !== false);
        return r.ok ? { continut: { pornit: true }, rezumat: "agentul planului a pornit" }
                    : { eroare: r.motiv ?? "agentul n-a pornit" };
      },
    },
  ];
}

/** Ce vede omul în loc de numele uneltei (limbaj obișnuit; v. `Asistent`). */
export const PASII_SITUATIEI: Record<string, { curge: string; gata: string }> = {
  citeste_planul: { curge: "asistent.pas.citesc_plan", gata: "asistent.pas.citit_plan" },
  preda_agentului_plan: { curge: "asistent.pas.predau_plan", gata: "asistent.pas.predat_plan" },
};
